// ============================================================
// SAUSAGE CALCULATOR — Core Calculation Logic
// ============================================================
import { MACRO_DB, SECTIONS } from '../data/ingredients';

// Casings are hardware, not formula. They are measured in feet of casing, never
// as a baker's percentage, and their weight is NOT part of the meat mix — so
// they must stay out of the formula weight, the stuffable weight, and the
// nutrition denominator. Cost is handled separately, per foot.
export const CASING_SECTION = 'casing';

export const isCasingRow = (row) => row?.sectionKey === CASING_SECTION;

// Feet of casing recorded on a casing row (rows store it as a string).
export function rowFeet(row) {
  const ft = parseFloat(row?.feet);
  return Number.isFinite(ft) && ft > 0 ? ft : 0;
}

// Convert meat weight to grams based on unit system
export function toGrams(value, unitSystem) {
  if (!value || isNaN(value)) return 0;
  return unitSystem === 'Imperial (lbs)'
    ? parseFloat(value) * 453.592
    : parseFloat(value);
}

// Calculate grams from baker's percentage and meat weight
export function pctToGrams(pct, meatWeightG) {
  if (!pct || !meatWeightG) return 0;
  return Math.round((pct / 100) * meatWeightG * 100) / 100;
}

// Calculate baker's percentage from grams and meat weight
export function gramsToPct(grams, meatWeightG) {
  if (!grams || !meatWeightG) return 0;
  return Math.round((grams / meatWeightG) * 100 * 1000) / 1000;
}

// Get ingredient status indicator
export function getStatus(name, pct, dbEntry) {
  if (!name || !pct || pct === 0) return null;
  if (!dbEntry) return null;
  const [, typMin, typMax, hardMax] = dbEntry;
  if (pct > hardMax) return { label: 'Over Max', type: 'danger' };
  if (pct > typMax)  return { label: 'High',     type: 'warn'   };
  if (pct >= typMin) return { label: 'In Range',  type: 'good'   };
  return               { label: 'Low',       type: 'low'    };
}

// Compute nitrite PPM from Cure #1 percentage
// PPM conversion factors by curing agent
const CURE_PPM_FACTORS = {
  'Prague Powder #1 (Cure #1)':       625,    // 6.25% sodium nitrite × pct × 100
  'Cultured Celery Juice Powder':      297.6,  // ~125ppm at 0.42% → factor = 125/0.42 = 297.6
  'EcoCure #1':                        100,    // ~100ppm at 1% per TSM spec
};

// A negative or unparseable percentage used to come back as a negative ppm,
// which isPPMSafe then called safe. Clamp the input at zero and let a bad one
// surface as null, so the caller has to say "check the cure amount" rather
// than show a reassuring badge for a number that means nothing.
export function calcPPM(curePct, ingredientName) {
  const factor = CURE_PPM_FACTORS[ingredientName] || 625;
  // An amount nobody entered is not "0 ppm of nitrite" — there is no figure to
  // report. A typed 0 is a real 0.
  if (curePct === null || curePct === undefined) return null;
  if (typeof curePct === 'string' && curePct.trim() === '') return null;
  const pct = Number(curePct);
  if (!Number.isFinite(pct) || pct < 0) return null;
  return Math.round(pct * factor * 10) / 10;
}

// Check if PPM is safe (USDA limit: 156 ppm, we flag > 156.5 to account for
// rounding). Anything that isn't a finite number is NOT safe: there is no
// figure to judge, so the cure amount needs checking.
export function isPPMSafe(ppm) {
  return Number.isFinite(ppm) && ppm >= 0 && ppm <= 156.5;
}

// Compute complete nutrition estimate from ingredient list
// ingDB is optional — if provided, custom ingredient macros are read from it
export function calcNutrition(ingredients, meatWeightG, ingDB) {
  if (!meatWeightG || meatWeightG === 0) {
    return { protein: 0, carbs: 0, fat: 0, sodium: 0, calories: 0,
             fatRatioTotal: 0, fatRatioMFOnly: 0 };
  }

  let totalWeight = 0;   // everything edible in the mix
  let knownWeight = 0;   // the part we have macro data for
  let totalProtein = 0, totalCarbs = 0, totalFat = 0;
  let totalSodium = 0, totalCalories = 0;
  let mfWeight = 0, mfFat = 0;
  const unknown = [];

  ingredients.forEach(ing => {
    const pctVal = parseFloat(ing.pct) || 0;
    if (!ing.name || !pctVal) return;
    if (isCasingRow(ing)) return;   // casing is not part of what's eaten by weight
    const grams = pctToGrams(pctVal, meatWeightG);
    // Prefer live DB macro (supports custom ingredients), fall back to static MACRO_DB
    const liveMacro = ingDB ? ingDB.getIngredient(ing.sectionKey, ing.name)?.macro : null;
    const macro = liveMacro || MACRO_DB[ing.name];
    totalWeight += grams;

    // An ingredient with no macro row must not enlarge the denominator: doing
    // that silently diluted every per-100g value, so a recipe using a binder or
    // spice we had no data for reported less protein, fat and sodium than it
    // actually contains. Weigh the estimate against what we can account for,
    // and report the coverage so a thin estimate is visible rather than quiet.
    if (!macro) {
      unknown.push(ing.name);
      return;
    }
    knownWeight += grams;

    {
      totalProtein  += grams * macro[0] / 100;
      totalCarbs    += grams * macro[1] / 100;
      totalFat      += grams * macro[2] / 100;
      totalSodium   += grams * macro[3] / 100;
      totalCalories += grams * macro[4] / 100;

      if (ing.sectionKey === 'meat' || ing.sectionKey === 'fat') {
        mfWeight += grams;
        mfFat    += grams * (macro[2] || 0) / 100;
      }
    }
  });

  if (knownWeight === 0) return { protein: 0, carbs: 0, fat: 0,
    sodium: 0, calories: 0, fatRatioTotal: 0, fatRatioMFOnly: 0,
    coverage: 0, unknownNames: unknown, unknownPct: 100 };

  return {
    protein:       Math.round(totalProtein  / knownWeight * 1000) / 10,
    carbs:         Math.round(totalCarbs    / knownWeight * 1000) / 10,
    fat:           Math.round(totalFat      / knownWeight * 1000) / 10,
    sodium:        Math.round(totalSodium   / knownWeight * 1000) / 10,
    calories:      Math.round(totalCalories / knownWeight * 1000) / 10,
    fatRatioTotal: Math.round(totalFat      / knownWeight * 10000) / 100,
    fatRatioMFOnly: mfWeight > 0
      ? Math.round(mfFat / mfWeight * 10000) / 100
      : 0,
    // Share of the mix's weight these numbers are actually based on
    coverage:   Math.round(knownWeight / totalWeight * 1000) / 10,
    unknownPct: Math.round((1 - knownWeight / totalWeight) * 1000) / 10,
    unknownNames: unknown,
  };
}

// Compute formula totals
// cookLossPct: weight lost in the smoker, as a % of stuffable weight. Typically
// 20-30% for smoked sausage; 25% is the usual working figure. Deliberately
// independent of the Pricing modal's own yield figure — this one is for
// formulating a batch to land on a target packaged weight, and the two are
// allowed to differ.
export function calcTotals(allIngredients, meatWeightG, equipmentLossG = 150,
                           cookLossPct = 25) {
  // Casings never contribute weight to the formula — they hold the mix, they
  // aren't in it. Drop them before anything is summed.
  const ingredients = (allIngredients || []).filter(ing => !isCasingRow(ing));
  const casingFeet  = (allIngredients || [])
    .filter(ing => isCasingRow(ing) && ing.name)
    .reduce((sum, ing) => sum + rowFeet(ing), 0);

  const allGrams = ingredients.reduce((sum, ing) => {
    if (!ing.name || !ing.pct) return sum;
    return sum + pctToGrams(parseFloat(ing.pct) || 0, meatWeightG);
  }, 0);

  const totalG    = Math.round(allGrams * 10) / 10;
  const stuffable = Math.max(0, Math.round((allGrams - equipmentLossG) * 10) / 10);
  const totalLb   = Math.round(totalG    / 453.592 * 1000) / 1000;
  const stuffLb   = Math.round(stuffable / 453.592 * 1000) / 1000;

  // What comes out of the smoker, and the 20-30% band around it
  const loss      = Math.min(99, Math.max(0, parseFloat(cookLossPct) || 0));
  const cookedG   = Math.round(stuffable * (1 - loss / 100) * 10) / 10;
  const cookedLb  = Math.round(cookedG / 453.592 * 1000) / 1000;
  const cookedLoG = Math.round(stuffable * 0.70 * 10) / 10;   // 30% loss
  const cookedHiG = Math.round(stuffable * 0.80 * 10) / 10;   // 20% loss

  // Meat + Fat totals
  // Always parseFloat — pct is stored as string from input fields
  const mfPct = ingredients.reduce((sum, ing) => {
    const p = parseFloat(ing.pct) || 0;
    if (!p) return sum;
    if (ing.sectionKey === 'meat' || ing.sectionKey === 'fat') return sum + p;
    return sum;
  }, 0);

  // Liquid total
  const liquidPct = ingredients.reduce((sum, ing) => {
    const p = parseFloat(ing.pct) || 0;
    if (!p) return sum;
    if (ing.sectionKey === 'liquid') return sum + p;
    return sum;
  }, 0);

  // Binder total (for liquid warning)
  const binderPct = ingredients.reduce((sum, ing) => {
    const p = parseFloat(ing.pct) || 0;
    if (!p) return sum;
    if (ing.sectionKey === 'binder') return sum + p;
    return sum;
  }, 0);

  // Spice total
  const spicePct = ingredients.reduce((sum, ing) => {
    const p = parseFloat(ing.pct) || 0;
    if (!p) return sum;
    if (ing.sectionKey === 'spice') return sum + p;
    return sum;
  }, 0);

  // Additions = everything except meat + fat
  const additionsPct = ingredients.reduce((sum, ing) => {
    const p = parseFloat(ing.pct) || 0;
    if (!p) return sum;
    if (ing.sectionKey === 'meat' || ing.sectionKey === 'fat') return sum;
    return sum + p;
  }, 0);

  const mfG         = Math.round(mfPct / 100 * meatWeightG * 10) / 10;
  const additionsG  = Math.round(additionsPct / 100 * meatWeightG * 10) / 10;

  return {
    totalG, totalLb, stuffable, stuffLb,
    cookLossPct:  loss,
    cookedG, cookedLb,
    cookedLoG, cookedHiG,
    cookedLoLb:   Math.round(cookedLoG / 453.592 * 1000) / 1000,
    cookedHiLb:   Math.round(cookedHiG / 453.592 * 1000) / 1000,
    casingFeet:   Math.round(casingFeet * 100) / 100,
    mfPct:        Math.round(mfPct * 1000) / 1000,
    mfG,
    additionsPct: Math.round(additionsPct * 1000) / 1000,
    additionsG,
    liquidPct: Math.round(liquidPct * 1000) / 1000,
    binderPct: Math.round(binderPct * 1000) / 1000,
    spicePct:  Math.round(spicePct  * 1000) / 1000,
  };
}

// Casing calculations — uses ft/lb as primary field (index 2 in CASING_DB)
// stuffableG: grams of stuffable mix
// linkLengthIn: desired link length in inches
// linkWeightG: user-calibrated avg sausage weight (null = derive from ft/lb)
// userFtPerLb: user override ft/lb (null = use DB value)
// cookLossPct: smoker loss, used to report what a link weighs once cooked
export function calcCasing(casingEntry, stuffableG, linkLengthIn = 6, linkWeightG = null,
                           userFtPerLb = null, cookLossPct = 25) {
  if (!casingEntry || !stuffableG) return null;

  const ftPerLb      = userFtPerLb > 0 ? userFtPerLb : casingEntry[2];
  const stuffableLb  = stuffableG / 453.592;

  const feetNeeded   = Math.round(stuffableLb * ftPerLb * 100) / 100;
  const metersNeeded = Math.round(feetNeeded * 0.3048 * 100)  / 100;
  const feetBuffer   = Math.round(feetNeeded   * 1.15 * 100)   / 100;
  const metersBuffer = Math.round(metersNeeded * 1.15 * 100)   / 100;

  // Predicted weight of one raw stuffed link, from ft/lb and link length alone.
  //   ft/lb says how many feet of casing one pound of mix fills, so
  //   1 ft of stuffed casing = 1/ftPerLb lb, and a link of L inches is L/12 ft:
  //     link lb = (L / 12) / ftPerLb   →   × 453.592 for grams
  // Independent of batch size — it describes the casing, not the batch.
  // A link length of 0 (or a blank one) means there is no link estimate to
  // make. Dividing by it used to put Infinity into the link count.
  const linkLenFt    = linkLengthIn > 0 ? linkLengthIn / 12 : 0;
  const predictedLinkWeightG = ftPerLb > 0 && linkLenFt > 0
    ? Math.round((linkLenFt / ftPerLb) * 453.592 * 10) / 10
    : 0;
  const predictedLinkWeightOz = Math.round(predictedLinkWeightG / 28.3495 * 100) / 100;
  const linksFromFt  = linkLenFt > 0 ? feetNeeded / linkLenFt : 0;
  const dbLinkWt     = predictedLinkWeightG;
  const effectiveLinkWt = linkWeightG > 0 ? linkWeightG : dbLinkWt;
  const estLinks     = effectiveLinkWt > 0
    ? Math.floor(stuffableG / effectiveLinkWt)
    : Math.round(linksFromFt);

  // What one link weighs out of the smoker — the number that decides whether a
  // package hits its label weight. Applies the same loss to whichever raw
  // weight is driving the link count.
  const loss          = Math.min(99, Math.max(0, parseFloat(cookLossPct) || 0));
  const cookedLinkG   = Math.round(effectiveLinkWt * (1 - loss / 100) * 10) / 10;
  const cookedLinkOz  = Math.round(cookedLinkG / 28.3495 * 100) / 100;

  return {
    ftPerLb,
    feetNeeded,
    metersNeeded,
    feetBuffer,
    metersBuffer,
    dbLinkWeightG:  dbLinkWt,
    predictedLinkWeightG,
    predictedLinkWeightOz,
    cookedLinkG,
    cookedLinkOz,
    cookLossPct: loss,
    // How many links the predicted weight implies, ignoring any manual override
    predictedLinks: predictedLinkWeightG > 0
      ? Math.floor(stuffableG / predictedLinkWeightG)
      : 0,
    effectiveLinkWt,
    estLinks,
    prepNote: casingEntry[3],
    source:   casingEntry[4] || '',
  };
}

// ── Working back from a finished weight ──────────────────────────────────
// Scaling by meat weight alone misses the target, because two things stand
// between the basis and what comes out of the smoker: equipment loss, which is
// a FIXED number of grams, and cook loss, which is a percentage. The fixed part
// is why no single multiplier works — the meat-to-finished ratio moves with
// batch size (about 1.30x at 2 lb finished, 1.17x at 25 lb).
//
//   total     = basis x totalPct/100
//   stuffable = total - equipLoss
//   cooked    = stuffable x (1 - cookLoss/100)
//
// Inverted, and exact — no iteration needed, since only the fixed loss breaks
// proportionality:
//
//   basis = (cooked / (1 - cookLoss/100) + equipLoss) / (totalPct/100)
//
// totalPct is the sum of every ingredient percentage (meat+fat plus additions),
// so it is normally above 100. Returns 0 when the inputs cannot give an answer.
export function solveMeatWeightForCooked(targetCookedG, totalPct, equipLossG = 150,
                                         cookLossPct = 25) {
  const target = parseFloat(targetCookedG) || 0;
  const pct    = parseFloat(totalPct)      || 0;
  const equip  = parseFloat(equipLossG)    || 0;
  const loss   = Math.min(99, Math.max(0, parseFloat(cookLossPct) || 0));
  if (target <= 0 || pct <= 0) return 0;

  const stuffableNeeded = target / (1 - loss / 100);
  const totalNeeded     = stuffableNeeded + equip;
  const basis           = totalNeeded / (pct / 100);
  return basis > 0 ? Math.round(basis * 10) / 10 : 0;
}

// Sum of every ingredient percentage in the formula. Casings carry no
// percentage and are skipped.
export function formulaTotalPct(ingredients) {
  return (ingredients || []).reduce((sum, ing) => {
    if (isCasingRow(ing) || !ing.name) return sum;
    return sum + (parseFloat(ing.pct) || 0);
  }, 0);
}

// ── Reading values back off a saved recipe ──────────────────────────────────
// Only '', null and undefined mean "not saved". Everything else is the cook's
// own value, 0 included — `||` used to turn a stored 0 into the default and
// silently change the recipe.
export function savedNumber(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback;
  const n = parseFloat(value);
  return Number.isFinite(n) ? value : fallback;
}

// Same, for the casing's index into CASING_DB, where 0 is a real casing
// (Natural Sheep 19-21mm) rather than "no casing selected".
export function savedIndex(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

// Generate a unique ID for ingredient rows
export function genId() {
  return Math.random().toString(36).slice(2, 9);
}

// Build a fresh empty ingredient row
export function emptyRow(sectionKey) {
  const row = { id: genId(), sectionKey, name: '', pct: '', note: '' };
  // Casings are entered as a length, taken from the Casing Calculator estimate
  if (sectionKey === CASING_SECTION) row.feet = '';
  return row;
}

// Build the initial ingredient list (all sections, default empty rows)
export function buildInitialIngredients() {
  return SECTIONS.flatMap(sect =>
    Array.from({ length: sect.rows }, () => emptyRow(sect.key))
  );
}
